import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import CollapsibleCard from '@core/components/CollapsibleCard';
import { fetchReportMembership } from '../api/membershipApi';
import type { MembershipTallies, ReportMembership } from '../types';
import { dayBeforeIso, formatCount, formatShortDate, formatShortDateTime } from '../utils/membershipFormat';
import UpdateMembershipModal from './UpdateMembershipModal';
import '../membership.css';

interface Props {
  churchId: number;
  departmentId: number;
  reportTagId: number;
  /** Called after a membership update is saved. */
  onSaved?: () => void;
  /** Changing this value reloads the figures, for example after the report status changes. */
  reloadToken?: string;
  /** The report can still be written to (not locked, editable status, tag open). */
  reportWritable?: boolean;
}

const TALLY_TILES: Array<{ key: keyof MembershipTallies; label: string; sign: '+' | '-' }> = [
  { key: 'added', label: 'Added', sign: '+' },
  { key: 'transferred_in', label: 'Transferred in', sign: '+' },
  { key: 'deceased', label: 'Deceased', sign: '-' },
  { key: 'transferred_out', label: 'Transferred out', sign: '-' },
  { key: 'excluded', label: 'Excluded', sign: '-' },
];

function shareOf(part?: number | null, total?: number | null): number {
  if (!total || !part) return 0;
  return Math.round((part / total) * 100);
}

function changesLabel(since: string, until: string): string {
  if (since.slice(0, 7) === until.slice(0, 7)) {
    const month = new Date(`${since.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    return `${month} changes`;
  }
  return 'Changes in this period';
}

const ReportMembershipSection: React.FC<Props> = ({ churchId, departmentId, reportTagId, onSaved, reloadToken, reportWritable = false }) => {
  const reportPath = `church/${churchId}/department/${departmentId}/report/reporttag/${reportTagId}`;
  const [membership, setMembership] = useState<ReportMembership | null>(null);
  const [showUpdate, setShowUpdate] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    fetchReportMembership(reportPath).then((response) => {
      if (cancelled) return;
      setMembership(response?.isSuccess() ? response.getData() ?? null : null);
    });
    return () => {
      cancelled = true;
    };
  }, [reportPath]);

  useEffect(() => {
    setMembership(null);
  }, [reportPath]);

  useEffect(() => load(), [load, reloadToken]);

  if (!membership?.enabled || !membership.since || !membership.until) return null;

  const { since, until } = membership;
  const sinceLabel = formatShortDate(since);
  const untilLabel = formatShortDate(until);
  const malePct = shareOf(membership.male_count, membership.total_members);
  const femalePct = shareOf(membership.female_count, membership.total_members);
  const tallies = membership.tallies;

  const canUpdate = reportWritable && membership.can_update_membership === true;

  const actions = canUpdate && (
    <div className="membership-card__actions">
      <Button variant="primary" size="sm" onClick={() => setShowUpdate(true)}>Update membership</Button>
    </div>
  );

  return (
    <CollapsibleCard
      className="ntcog-card dashboard-section-card membership-card membership-card--modern membership-card--report mb-4"
      title="Membership"
      subtitle={`Church membership for ${sinceLabel} \u2013 ${untilLabel}`}
      subtitleClassName="membership-subtitle text-muted small mb-0"
      actions={actions}
      bodyId="report-membership-body"
      footer={
        canUpdate && (
          <UpdateMembershipModal
            show={showUpdate}
            apiPath={`church/${churchId}`}
            submitPath={reportPath}
            minDate={since}
            maxDate={until}
            dateHint={`Between ${sinceLabel} and ${untilLabel}, this report's period`}
            onHide={() => setShowUpdate(false)}
            onSaved={() => {
              load();
              onSaved?.();
            }}
          />
        )
      }
    >
      <div className="membership-stats membership-stats--3">
        <div className="membership-stat">
          <div className="membership-stat__label">Total members as at {untilLabel}</div>
          <div className="membership-stat__value">{formatCount(membership.total_members)}</div>
          {membership.opening_total != null && (
            <div className="membership-stat__note">
              {formatCount(membership.opening_total)} at {formatShortDate(dayBeforeIso(since))}
            </div>
          )}
        </div>
        <div className="membership-stat">
          <div className="membership-stat__label">Male</div>
          <div className="membership-stat__value">{formatCount(membership.male_count)}</div>
          <div className="membership-stat__bar" aria-hidden="true"><span style={{ width: `${malePct}%` }} /></div>
          <div className="membership-stat__note">{malePct}% of members</div>
        </div>
        <div className="membership-stat">
          <div className="membership-stat__label">Female</div>
          <div className="membership-stat__value">{formatCount(membership.female_count)}</div>
          <div className="membership-stat__bar" aria-hidden="true"><span style={{ width: `${femalePct}%` }} /></div>
          <div className="membership-stat__note">{femalePct}% of members</div>
        </div>
      </div>

      <h3 className="membership-section-label">{changesLabel(since, until)}</h3>
      <p className="membership-hint mb-2">Changes recorded in this period. Zeros mean nothing was recorded.</p>
      <div className="membership-records">
        {TALLY_TILES.map((tile) => (
          <div className="membership-record" key={tile.key}>
            <div className="membership-record__label">
              <span
                className={`membership-record__sign membership-record__sign--${tile.sign === '+' ? 'in' : 'out'}`}
                aria-hidden="true"
              >
                {tile.sign === '+' ? '+' : '\u2212'}
              </span>
              {tile.label}
            </div>
            <div className="membership-record__value">{formatCount(tallies?.[tile.key] ?? 0)}</div>
          </div>
        ))}
      </div>

      <div className="membership-updated-summary mt-3">
        Last updated: {membership.last_updated_at ? formatShortDateTime(membership.last_updated_at) : 'Not updated yet'}
      </div>
    </CollapsibleCard>
  );
};

export default ReportMembershipSection;
